import { useEffect, useRef } from "react";

/**
 * useLivePoll — fires `tick()` every `intervalMs` for as long as `enabled` is
 * truthy AND the browser tab is visible. Bulletproof against dev-server hot
 * reloads (we always clear on unmount / dep change), and mid-poll errors are
 * swallowed so the timer never dies.
 *
 * The mobile Pit Link app writes back to the same production API the portal
 * reads. This poll is what keeps the two mirrors in near real-time — every
 * list page in the portal is wrapped in it.
 *
 * @param {() => (Promise<any> | any)} tick     Fetch function (usually your `reload`).
 * @param {number} intervalMs                   Poll cadence in ms.
 * @param {boolean} enabled                     Set false to pause polling.
 */
export function useLivePoll(tick, intervalMs = 5000, enabled = true) {
  const tickRef = useRef(tick);
  tickRef.current = tick;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      try { await tickRef.current?.(); } catch { /* keep polling */ }
    };

    const id = setInterval(run, intervalMs);
    const onVis = () => { if (document.visibilityState === "visible") run(); };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intervalMs, enabled]);
}
